import { experimental_extendTheme as extendTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import cssVarJson from './export.json';
import InterItalic from './fonts/Inter/InterVariable-Italic.woff2';
import Inter from './fonts/Inter/InterVariable.woff2';
import { fixThemeOptions } from '../../utils/theme';
const CSS_VAR_PREFIX = 'umeds'; // Needs to be unique to support theme nesting
const baseFontFamily = 'Inter, Helvetica, Arial, sans-serif';
const customizations = {
    cssVarPrefix: CSS_VAR_PREFIX,
    spacing: (factor) => `${0.25 * factor}rem`,
    typography: {
        fontFamily: baseFontFamily,
    },
    components: {
        // MuiAlert overrides to fix the broken logic in extend theme.
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-error-light)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`, // Different color for message text
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardWarning: {
                    backgroundColor: '#FFF4E5',
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardInfo: {
                    backgroundColor: '#E5F6FD',
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                standardSuccess: {
                    backgroundColor: '#EDF7ED',
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedError: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-error-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-error-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedWarning: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-warning-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-warning-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedInfo: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-info-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-info-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
                outlinedSuccess: {
                    borderColor: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    color: `var(--${CSS_VAR_PREFIX}-palette-success-main)`,
                    '& .MuiAlert-message': {
                        color: `var(--${CSS_VAR_PREFIX}-palette-success-dark)`,
                    },
                    '& .MuiAlert-action': {
                        '& .MuiButtonBase-root.MuiButton-root.MuiButton-text.MuiButton-textInherit': {
                            color: 'inherit',
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
        @font-face {
          font-family: Inter;
          font-style: normal;
          font-weight: 100 900;
          font-display: swap;
          src: url(${Inter}) format("woff2");
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        @font-face {
          font-family: Inter;
          font-style: italic;
          font-weight: 100 900;
          font-display: swap;
          src: url(${InterItalic}) format("woff2");
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        .Toastify__toast--success { background-color: #EDF7ED; }
        .Toastify__toast--error { background-color: #F9E6E6; }
      `,
        },
        // Temporary fixes to the export. Once the figma is fixed and exported properly, these should be removed
        MuiChip: {
            styleOverrides: {
                label: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                sizeLarge: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px', // Export bug causing button hover padding change
                }),
                sizeMedium: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px',
                }),
                sizeSmall: ({ ownerState }) => ({
                    padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px',
                }),
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: (props) => ({
                    ...props.theme.typography.caption,
                }),
            },
        },
        MuiLink: {
            styleOverrides: {
                button: {
                    verticalAlign: 'unset',
                },
            },
        },
    },
};
export const uMedsJSON = fixThemeOptions(deepmerge(cssVarJson, customizations));
export const uMeds = extendTheme(uMedsJSON);
export default uMeds;
//# sourceMappingURL=data:application/json;base64,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